"use client";

import { useState, useEffect, type FormEvent } from "react";
import { useSearchParams } from "next/navigation";
import { loginAction } from "~/server/actions/auth-actions";
import Link from "next/link";
import { EXTERNAL_URLS } from "~/lib/external-urls";
import { api } from "~/trpc/react";
import { ArelloLogo, ArelloBackground } from "~/components/icons";

export default function LoginPage() {
  const searchParams = useSearchParams();
  const { data: statistics } = api.statistics.getPublicStatistics.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  useEffect(() => {
    const sessionStatus = searchParams.get("session");
    if (sessionStatus === "expired") {
      setSessionExpiredMessage("Your session has expired. Please log in again.");
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError("");
    setSessionExpiredMessage("");
    setIsLoading(true);

    try {
      const result = await loginAction(username, password);

      if (result.success) {
        const callbackUrl = searchParams.get("callbackUrl") ?? "/";
        window.location.href = callbackUrl;
      } else {
        setLoginError(result.error ?? "Login failed. Please try again.");
        setIsLoading(false);
      }
    } catch {
      setLoginError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start bg-white">
      {/* Left Side - Brand and Features */}
      <div className="hidden lg:flex lg:flex-1 relative h-screen overflow-auto">
        {/* Background */}
        <div className="absolute inset-0">
          <ArelloBackground
            className="absolute inset-0 w-full h-full"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-8 py-12">
          <div className="max-w-[624px] w-full space-y-12">
            {/* Header Section */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-end gap-3">
                    <h1
                      className="text-[56px] leading-[68px] tracking-[-0.4px] text-white font-normal"
                      style={{ fontFamily: "Optima, serif" }}
                    >
                      Welcome to
                    </h1>
                    <div className="h-16 w-[235px] relative">
                      <ArelloLogo variant="light" className="w-full h-full" />
                    </div>
                  </div>
                  <p className="text-2xl italic text-[#fefdfc] tracking-[-0.1px] leading-[30px]">
                    Disciplinary Action Database
                  </p>
                </div>
                <p className="text-xl font-light text-[#fefdfc] tracking-[-0.08px] leading-7">
                  {statistics
                    ? `${formatNumber((statistics as { totalRecords: number; totalJurisdictions: number; totalOrganizations: number }).totalRecords)} Records across ${formatNumber((statistics as { totalRecords: number; totalJurisdictions: number; totalOrganizations: number }).totalJurisdictions)} Jurisdictions`
                    : "Loading statistics..."}
                </p>
              </div>

              <p className="text-base text-[rgba(255,255,255,0.8)] leading-6">
                A comprehensive web-based interface for real estate licensing authorities to share and access disciplinary action information.
              </p>
            </div>

            {/* Features List */}
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-md bg-[rgba(221,234,248,0.08)] flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 13.3333V10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 6.66667H10.0083"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-white leading-6">
                    Multi-Jurisdictional Access
                  </h3>
                  <p className="text-sm text-[rgba(255,255,255,0.8)] leading-5">
                    Connect with licensing authorities across jurisdictions
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-md bg-[rgba(221,234,248,0.08)] flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 1.66667L12.575 6.9L18.3334 7.73333L14.1667 11.7833L15.15 17.5167L10 14.8083L4.85002 17.5167L5.83335 11.7833L1.66669 7.73333L7.42502 6.9L10 1.66667Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-white leading-6">
                    Secure & Compliant
                  </h3>
                  <p className="text-sm text-[rgba(255,255,255,0.8)] leading-5">
                    Enterprise-grade security for sensitive information
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-md bg-[rgba(221,234,248,0.08)] flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M15 8.33333V6.66667C15 4.08934 12.7614 2 10 2C7.23862 2 5 4.08934 5 6.66667V8.33333M10 12.5V14.1667M6.66667 18.3333H13.3333C14.2538 18.3333 15 17.5871 15 16.6667V10C15 9.07953 14.2538 8.33333 13.3333 8.33333H6.66667C5.74619 8.33333 5 9.07953 5 10V16.6667C5 17.5871 5.74619 18.3333 6.66667 18.3333Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-white leading-6">
                    Comprehensive Records
                  </h3>
                  <p className="text-sm text-[rgba(255,255,255,0.8)] leading-5">
                    Access detailed disciplinary action histories
                  </p>
                </div>
              </div>

              <Link
                href={EXTERNAL_URLS.ARELLO.PROGRAMS.DADB}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-base font-semibold text-[rgba(171,214,255,0.88)] hover:text-[rgba(171,214,255,1)] transition-colors"
              >
                Learn more
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3.5 8H12.5M12.5 8L8.5 4M12.5 8L8.5 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>

            {/* Footer */}
            <div className="pt-8">
              <p className="text-xs text-[rgba(246,249,253,0.6)] leading-[18px]">
                © 2025 ARELLO. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[640px] h-screen flex items-center justify-center px-6 py-12 bg-[#fcfdfe]">
        <div className="w-full max-w-[420px] space-y-8">
          {/* Mobile Header - Only shown on small screens */}
          <div className="lg:hidden text-center space-y-4 mb-12">
            <div className="flex flex-col items-center gap-2">
              <h1
                className="text-4xl leading-tight tracking-[-0.4px] text-[#1c2024] font-normal"
                style={{ fontFamily: "Optima, serif" }}
              >
                Welcome to
              </h1>
              <div className="h-14 w-[180px] relative">
                <ArelloLogo variant="dark" className="w-full h-full" />
              </div>
            </div>
            <p className="text-lg italic text-[#1c2024] tracking-[-0.1px]">
              Disciplinary Action Database
            </p>
          </div>

          {/* Form Header */}
          <div className="space-y-2">
            <h2 className="text-[28px] font-semibold text-[#1c2024] leading-9 tracking-[-0.12px]">
              Sign In
            </h2>
            <p className="text-sm text-[#60646c] leading-5">
              Enter your credentials to access the database
            </p>
          </div>

          {sessionExpiredMessage && (
            <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-md flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-sm text-yellow-800">{sessionExpiredMessage}</p>
            </div>
          )}

          {loginError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-sm text-red-800">{loginError}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div className="space-y-2">
              <label
                htmlFor="username"
                className="block text-sm font-semibold text-[#1c2024] leading-5"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
                className="w-full h-8 px-2 py-0 text-sm bg-[rgba(255,255,255,0.9)] border border-[rgba(0,9,50,0.12)] rounded text-[#1c2024] placeholder:text-[rgba(0,5,29,0.45)] focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:border-transparent"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-[#1c2024] leading-5"
                >
                  Password
                </label>
                <Link
                  href="/forgot-password"
                  className="text-sm font-semibold text-[rgba(0,50,97,0.7)] hover:text-[rgba(0,50,97,0.9)] transition-colors leading-5"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full h-8 px-2 py-0 pr-10 text-sm bg-[rgba(255,255,255,0.9)] border border-[rgba(0,9,50,0.12)] rounded text-[#1c2024] placeholder:text-[rgba(0,5,29,0.45)] focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-4 h-4 text-[#60646c]" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M1.61342 8.47467C1.52262 8.33686 1.47723 8.26796 1.45182 8.15398C1.43273 8.06822 1.43273 7.93178 1.45182 7.84602C1.47723 7.73204 1.52262 7.66314 1.61341 7.52533C2.36369 6.33791 4.59693 3.33334 8.00027 3.33334C11.4036 3.33334 13.6369 6.33791 14.3871 7.52533C14.4779 7.66314 14.5233 7.73204 14.5487 7.84602C14.5678 7.93178 14.5678 8.06822 14.5487 8.15398C14.5233 8.26796 14.4779 8.33686 14.3871 8.47467C13.6369 9.66209 11.4036 12.6667 8.00027 12.6667C4.59693 12.6667 2.36369 9.66209 1.61342 8.47467Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.00027 10C9.10484 10 10.0003 9.10457 10.0003 8C10.0003 6.89543 9.10484 6 8.00027 6C6.8957 6 6.00027 6.89543 6.00027 8C6.00027 9.10457 6.8957 10 8.00027 10Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-[#60646c]" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M7.05394 3.40599C7.35893 3.3686 7.67693 3.33333 8.00027 3.33333C11.4036 3.33333 13.6369 6.33791 14.3871 7.52533C14.4779 7.66314 14.5233 7.73204 14.5487 7.84602C14.5678 7.93178 14.5678 8.06822 14.5487 8.15398C14.5233 8.26796 14.4779 8.33686 14.3871 8.47467C14.0994 8.92054 13.6831 9.50617 13.1536 10.0933M4.42094 4.42133C3.08427 5.36866 2.17694 6.58799 1.61342 7.52533C1.52262 7.66314 1.47723 7.73204 1.45182 7.84602C1.43273 7.93178 1.43273 8.06822 1.45182 8.15398C1.47723 8.26796 1.52262 8.33686 1.61341 8.47467C2.36369 9.66209 4.59693 12.6667 8.00027 12.6667C9.30227 12.6667 10.4316 12.1587 11.3949 11.575M2.00027 2L14.0003 14M6.58605 6.58579C6.22785 6.94398 6.00027 7.44129 6.00027 8C6.00027 9.10457 6.8957 10 8.00027 10C8.55898 10 9.05629 9.77243 9.41449 9.41421"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center gap-2">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 border border-[rgba(0,6,46,0.2)] rounded-sm bg-[rgba(255,255,255,0.9)] checked:bg-[#1c3144] checked:border-[#1c3144] focus:ring-2 focus:ring-[#1c3144] focus:ring-offset-0 cursor-pointer"
              />
              <label
                htmlFor="remember-me"
                className="text-sm text-[#1c2024] leading-5 cursor-pointer select-none"
              >
                Remember me
              </label>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-10 px-4 bg-[#1c3144] text-[#f6f9fd] text-base font-semibold rounded-md hover:bg-[#2a4558] focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Contact Link */}
          <div className="pt-6 border-t border-[rgba(0,0,45,0.09)] text-center">
            <p className="text-sm text-[rgba(0,0,0,0.7)] leading-5">
              Don&apos;t have an account?{" "}
              <Link
                href={EXTERNAL_URLS.ARELLO.ABOUT.CONTACT}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[rgba(0,50,97,0.7)] hover:text-[rgba(0,50,97,0.9)] transition-colors"
              >
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

